import { useState, useCallback } from 'react';
import ApiService from '../services/api';
import logger from '../utils/logger';

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  if (!(file instanceof File)) {
    reject(new Error('Please select a valid image file'));
    return;
  }
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Failed to read the selected image'));
  reader.readAsDataURL(file);
});

const extractJsonPayload = (text = '') => {
  if (!text) return null;
  const trimmed = text.trim().replace(/```json/gi, '').replace(/```/g, '').trim();
  const match = trimmed.match(/\{[\s\S]*\}/);
  if (!match) return null;
  try {
    return JSON.parse(match[0]);
  } catch (error) {
    logger.warn('[VisionPostGenerator] Unable to parse JSON payload', { snippet: match[0] });
    return null;
  }
};

const sanitizeKeywords = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items
    .map(item => String(item || '').trim())
    .filter(Boolean)
    .map(item => item.toLowerCase())
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .slice(0, 5);
};

const sanitizeHashtags = (items = []) => {
  if (!Array.isArray(items)) return [];
  return items
    .map(item => String(item || '').trim())
    .filter(Boolean)
    .map(item => (item.startsWith('#') ? item : `#${item}`))
    .map(item => `#${item.slice(1).replace(/[^a-z0-9]/gi, '')}`)
    .filter(item => item.length > 1)
    .map(item => `#${item.slice(1, 28)}`)
    .filter((item, index, arr) => item && arr.indexOf(item) === index)
    .slice(0, 8);
};

const initialState = {
  result: null,
  error: null,
  isGenerating: false,
};

export const useVisionPostGenerator = () => {
  const [state, setState] = useState(initialState);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const generateFromImage = useCallback(async (file, context = {}) => {
    setState(prev => ({ ...prev, isGenerating: true, error: null }));
    try {
      const base64 = await fileToDataUrl(file);
      const response = await ApiService.post('/customer/ai/describe-image', {
        imageBase64: base64,
        context,
      });

      const payload = response?.data || {};
      const structured = payload.structured || extractJsonPayload(payload.text || payload.raw || '');

      if (!structured || !structured.post) {
        throw new Error('AI did not return a usable post. Please try again with another image.');
      }

      const normalized = {
        post: String(structured.post || '').trim(),
        keywords: sanitizeKeywords(structured.keywords),
        hashtags: sanitizeHashtags(structured.hashtags),
      };

      if (!normalized.keywords.length) {
        normalized.keywords = sanitizeKeywords((context?.category ? [`${context.category}`] : []).concat(context?.city ? [`${context.category || 'business'} in ${context.city}`] : []));
      }

      setState({ result: normalized, error: null, isGenerating: false });
      return normalized;
    } catch (error) {
      const message = error?.message || 'Failed to generate post from the selected image.';
      logger.error('[VisionPostGenerator] Generation failed:', message);
      setState({ result: null, error: message, isGenerating: false });
      throw error;
    }
  }, []);

  return {
    result: state.result,
    error: state.error,
    isGenerating: state.isGenerating,
    generateFromImage,
    reset,
  };
};
